import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  DefaultLegendContentProps,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartMode, TimelinePoint } from '../../utils/salesActivityTimeline';
import { getSourceColor } from '../../utils/sourceColors';

export interface SourceSeries {
  source: string;
  color: string;
  dataKey: string;
}

interface Props {
  data: TimelinePoint[];
  mode: ChartMode;
  formatCurrency: (n: number) => string;
  resetToken?: string;
  onPeriodClick?: (periodKey: string) => void;
}

type ChartRow = Record<string, unknown> & {
  label: string;
  periodKey: string;
  x: number;
  wonRevenue: number;
  changeOrderRevenue: number;
  wonCount: number;
  lostCount: number;
  changeOrderCount: number;
  designFees: number;
  leads: number;
  _raw: TimelinePoint;
};

const CHART_Y_AXIS_PX = 70;
const CHART_RIGHT_MARGIN_PX = 20;
const CHART_RIGHT_AXIS_PX = 56;
const CHART_MIN_ZOOM_POINTS = 3;

function sourceDataKey(source: string): string {
  return `src_${source.replace(/[^a-zA-Z0-9]+/g, '_')}`;
}

export function buildSourceSeries(data: TimelinePoint[]): SourceSeries[] {
  const totals = new Map<string, number>();
  data.forEach(p => {
    Object.entries(p.leads.bySource).forEach(([src, count]) => {
      totals.set(src, (totals.get(src) || 0) + count);
    });
  });
  return Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([source]) => ({
      source,
      color: getSourceColor(source),
      dataKey: sourceDataKey(source),
    }));
}

const formatAxisCurrency = (value: number) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${value}`;
};

interface ActivityChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: Array<{
    name?: string;
    value?: number | string | null;
    color?: string;
    dataKey?: string | number;
    payload?: ChartRow;
  }>;
  showLeads: boolean;
  showSales: boolean;
  formatCurrency: (n: number) => string;
}

const ActivityChartTooltip: React.FC<ActivityChartTooltipProps> = ({
  active,
  label,
  payload,
  showLeads,
  showSales,
  formatCurrency,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<'above' | 'below'>('above');
  const raw = payload?.[0]?.payload?._raw;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el) return;

    const wrapper = el.closest('.recharts-tooltip-wrapper') as HTMLElement | null;
    if (!wrapper) return;

    el.style.transform = 'none';

    const margin = 8;
    const gap = 10;
    const headerBottom =
      document.querySelector('.app-header')?.getBoundingClientRect().bottom ?? 0;
    const topLimit = Math.max(margin, headerBottom + margin);
    const rect = wrapper.getBoundingClientRect();
    const height = el.offsetHeight;
    const width = el.offsetWidth;

    const spaceAbove = rect.top - topLimit;
    const spaceBelow = window.innerHeight - rect.top - margin;
    const next: 'above' | 'below' =
      height + gap <= spaceAbove || spaceAbove >= spaceBelow ? 'above' : 'below';

    const desiredTop = next === 'above' ? rect.top - gap - height : rect.top + gap;
    const maxTop = window.innerHeight - margin - height;
    const clampedTop = Math.min(Math.max(desiredTop, topLimit), Math.max(topLimit, maxTop));

    const desiredLeft = rect.left;
    const maxLeft = window.innerWidth - margin - width;
    const clampedLeft = Math.min(Math.max(desiredLeft, margin), Math.max(margin, maxLeft));
    const nextX = clampedLeft - desiredLeft;
    const nextY = clampedTop - desiredTop;

    el.style.transform =
      next === 'above'
        ? `translate(${nextX}px, calc(-100% - 10px + ${nextY}px))`
        : `translate(${nextX}px, ${10 + nextY}px)`;

    setPlacement(prev => (prev === next ? prev : next));
  }, [active, label, payload]);

  if (!active || !payload?.length || !raw) return null;

  const sourceEntries = Object.entries(raw.leads.bySource)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div
      ref={ref}
      className={`fin-forecast-chart-tooltip fin-forecast-chart-tooltip--${placement}`}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{raw.label}</div>
      {showLeads && (
        <div style={{ marginBottom: 4 }}>
          <div style={{ color: '#6B6960', marginBottom: 2 }}>
            Leads: {raw.leads.count}
          </div>
          {sourceEntries.map(([src, count]) => (
            <div key={src} style={{ color: getSourceColor(src), marginBottom: 2 }}>
              {src}: {count}
            </div>
          ))}
        </div>
      )}
      {showSales && (
        <div style={{ marginTop: showLeads ? 6 : 0, paddingTop: showLeads ? 6 : 0, borderTop: showLeads ? '1px solid #E2E0DB' : undefined }}>
          <div style={{ color: '#166534', marginBottom: 2 }}>
            Won revenue: {formatCurrency(raw.sales.wonRevenue)}
          </div>
          <div style={{ color: '#0F766E', marginBottom: 2 }}>
            Deals won: {raw.sales.wonCount}
          </div>
          {raw.sales.lostCount > 0 && (
            <div style={{ color: '#991B1B', marginBottom: 2 }}>
              Deals lost: {raw.sales.lostCount}
            </div>
          )}
          {raw.sales.changeOrderCount > 0 && (
            <div style={{ color: '#92400E', marginBottom: 2 }}>
              Change orders: {formatCurrency(raw.sales.changeOrderRevenue)} ({raw.sales.changeOrderCount})
            </div>
          )}
          {raw.sales.designFees > 0 && (
            <div style={{ color: '#6B6960', marginBottom: 2 }}>
              Design fees: {formatCurrency(raw.sales.designFees)}
            </div>
          )}
        </div>
      )}
      <div style={{ fontSize: 10, color: '#6B6960', marginTop: 6 }}>
        Click the day for the itemized breakdown
      </div>
    </div>
  );
};

const SalesActivityTimelineChart: React.FC<Props> = ({
  data,
  mode,
  formatCurrency,
  resetToken,
  onPeriodClick,
}) => {
  const sourceSeries = useMemo(() => buildSourceSeries(data), [data]);
  const showLeads = mode === 'leads' || mode === 'both';
  const showSales = mode === 'sales' || mode === 'both';
  const rightMargin = showLeads && showSales ? CHART_RIGHT_AXIS_PX : CHART_RIGHT_MARGIN_PX;

  const chartData = useMemo<ChartRow[]>(
    () =>
      data.map((p, i) => {
        const row: ChartRow = {
          label: p.label,
          periodKey: p.periodKey,
          x: i,
          wonRevenue: Math.round(p.sales.wonRevenue),
          changeOrderRevenue: Math.round(p.sales.changeOrderRevenue),
          wonCount: p.sales.wonCount,
          lostCount: p.sales.lostCount,
          changeOrderCount: p.sales.changeOrderCount,
          designFees: Math.round(p.sales.designFees),
          leads: p.leads.count,
          _raw: p,
        };
        sourceSeries.forEach(s => {
          row[s.dataKey] = p.leads.bySource[s.source] || 0;
        });
        return row;
      }),
    [data, sourceSeries],
  );

  const [zoomWindow, setZoomWindow] = useState<{ start: number; end: number } | null>(null);
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const chartLengthRef = useRef(0);
  const zoomWindowRef = useRef(zoomWindow);
  const zoomRafRef = useRef(0);
  chartLengthRef.current = chartData.length;

  const resetZoom = useCallback(() => {
    zoomWindowRef.current = null;
    setZoomWindow(null);
  }, []);

  useEffect(() => {
    resetZoom();
  }, [resetToken, data.length, mode, resetZoom]);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const n = Math.max(1, chartLengthRef.current);
      const rect = el.getBoundingClientRect();
      const plotLeft = rect.left + CHART_Y_AXIS_PX;
      const plotWidth = Math.max(1, rect.width - CHART_Y_AXIS_PX - rightMargin);
      const cursorFrac = Math.min(1, Math.max(0, (e.clientX - plotLeft) / plotWidth));

      const prev = zoomWindowRef.current;
      const start = prev?.start ?? 0;
      const end = prev?.end ?? 1;
      const span = Math.max(1e-6, end - start);

      let ns: number;
      let ne: number;
      const isPan = !e.ctrlKey && Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (isPan) {
        if (!prev) return;
        const shift = (e.deltaX / plotWidth) * span;
        ns = start + shift;
        ne = end + shift;
      } else {
        let dy = e.deltaY;
        if (e.deltaMode === 1) dy *= 16;
        else if (e.deltaMode === 2) dy *= plotWidth;
        const clamped = Math.max(-80, Math.min(80, dy));
        const factor = Math.exp(clamped * 0.00115);
        const minSpan = Math.min(1, CHART_MIN_ZOOM_POINTS / n);
        const newSpan = Math.min(1, Math.max(minSpan, span * factor));
        const anchor = start + cursorFrac * span;
        ns = anchor - cursorFrac * newSpan;
        ne = ns + newSpan;
      }

      if (ns < 0) {
        ne -= ns;
        ns = 0;
      }
      if (ne > 1) {
        ns -= ne - 1;
        ne = 1;
      }
      ns = Math.max(0, Math.min(ns, 1));
      ne = Math.max(ns, Math.min(ne, 1));
      const next = ns <= 0.0005 && ne >= 0.9995 ? null : { start: ns, end: ne };
      zoomWindowRef.current = next;
      if (zoomRafRef.current) return;
      zoomRafRef.current = requestAnimationFrame(() => {
        zoomRafRef.current = 0;
        setZoomWindow(zoomWindowRef.current);
      });
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
      if (zoomRafRef.current) cancelAnimationFrame(zoomRafRef.current);
    };
  }, [rightMargin]);

  const xDomain = useMemo<[number, number]>(() => {
    const n = chartData.length;
    if (n <= 1) return [-0.5, 0.5];
    if (!zoomWindow) return [-0.5, n - 0.5];
    return [zoomWindow.start * n - 0.5, zoomWindow.end * n - 0.5];
  }, [chartData.length, zoomWindow]);

  const xTicks = useMemo(() => {
    const n = chartData.length;
    if (n === 0) return [];
    const lo = xDomain[0];
    const hi = xDomain[1];
    const first = Math.max(0, Math.ceil(lo));
    const last = Math.min(n - 1, Math.floor(hi));
    const span = Math.max(1, last - first + 1);
    const step = span <= 14 ? 1 : Math.ceil(span / 14);
    const ticks: number[] = [];
    for (let i = first; i <= last; i += step) ticks.push(i);
    return ticks;
  }, [chartData.length, xDomain]);

  const formatXTick = useCallback(
    (v: number) => chartData[Math.round(v)]?.label ?? '',
    [chartData],
  );

  const visibleRows = useMemo(() => {
    if (!zoomWindow) return chartData;
    const lo = xDomain[0] - 1;
    const hi = xDomain[1] + 1;
    return chartData.filter(row => row.x >= lo && row.x <= hi);
  }, [chartData, xDomain, zoomWindow]);

  const leadsYDomain = useMemo<[number, number]>(() => {
    const max = visibleRows.reduce((m, row) => Math.max(m, Number(row.leads) || 0), 0);
    return [0, Math.max(1, Math.ceil(max + Math.max(max * 0.08, 1)))];
  }, [visibleRows]);

  const salesYDomain = useMemo<[number, number]>(() => {
    const max = visibleRows.reduce(
      (m, row) => Math.max(m, (Number(row.wonRevenue) || 0) + (Number(row.changeOrderRevenue) || 0)),
      0,
    );
    const pad = Math.max(max * 0.08, max > 0 ? 1000 : 1000);
    return [0, max + pad];
  }, [visibleRows]);

  const zoomRangeLabel = useMemo(() => {
    if (!zoomWindow || chartData.length === 0) return null;
    const n = chartData.length;
    const firstIdx = Math.min(n - 1, Math.max(0, Math.ceil(zoomWindow.start * n - 1e-6)));
    const lastIdx = Math.min(n - 1, Math.max(0, Math.floor(zoomWindow.end * n - 1e-6)));
    const first = chartData[firstIdx]?.label;
    const last = chartData[lastIdx]?.label;
    if (!first || !last) return null;
    return first === last ? first : `${first} – ${last}`;
  }, [zoomWindow, chartData]);

  const handleChartClick = useCallback(
    (state: {
      activeLabel?: string | number;
      activePayload?: Array<{ payload?: ChartRow }>;
    } | null) => {
      const fromPayload = state?.activePayload?.[0]?.payload;
      const fromX =
        typeof state?.activeLabel === 'number'
          ? chartData[Math.round(state.activeLabel)]
          : chartData.find(r => r.label === String(state?.activeLabel ?? ''));
      const row = fromPayload ?? fromX;
      if (!row?.periodKey) return;
      onPeriodClick?.(row.periodKey);
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && chartWrapRef.current?.contains(focused)) {
        focused.blur();
      }
    },
    [chartData, onPeriodClick],
  );

  const hideLeadLegendEntries = showLeads && sourceSeries.length > 0;

  const renderLegend = (props: DefaultLegendContentProps) => {
    const payload = props.payload;
    const items =
      hideLeadLegendEntries && showSales
        ? (payload || []).filter(
            entry =>
              entry.value === 'Won revenue' ||
              entry.value === 'Change orders' ||
              entry.value === 'Deals won',
          )
        : payload || [];
    if (!items.length) return null;
    return (
      <ul
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '10px 16px',
          padding: '8px 0 0',
          margin: 0,
          listStyle: 'none',
          fontSize: 11,
          color: '#6B6960',
        }}
      >
        {items.map(entry => (
          <li key={String(entry.value)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 10,
                height: entry.type === 'line' ? 2 : 10,
                background: entry.color,
                display: 'inline-block',
                borderRadius: entry.type === 'line' ? 0 : 2,
              }}
            />
            {entry.value}
          </li>
        ))}
      </ul>
    );
  };

  if (data.length === 0) {
    return <div className="fin-proj-detail-empty">No data for this range</div>;
  }

  return (
    <div>
      <div className="sa-chart-zoom-row">
        {zoomWindow ? (
          <>
            {zoomRangeLabel && <span className="fin-balance-meta">{zoomRangeLabel}</span>}
            <button
              type="button"
              className="fin-secondary-btn"
              onClick={resetZoom}
              aria-label="Reset chart zoom"
            >
              Reset zoom
            </button>
          </>
        ) : (
          <span className="fin-balance-meta">Scroll over the chart to zoom</span>
        )}
      </div>
      <div
        ref={chartWrapRef}
        className="fin-forecast-chart-wrap"
        style={{ width: '100%', height: 360, minHeight: 360 }}
        onDoubleClick={resetZoom}
        title={
          zoomWindow
            ? 'Scroll to zoom · horizontal scroll to pan · double-click to reset'
            : 'Scroll to zoom'
        }
      >
        <ResponsiveContainer width="100%" height="100%" minHeight={360}>
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: rightMargin, bottom: 0, left: 0 }}
            onClick={handleChartClick}
            accessibilityLayer={false}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              type="number"
              dataKey="x"
              domain={xDomain}
              allowDataOverflow
              ticks={xTicks}
              tickFormatter={formatXTick}
              tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
              padding={{ left: 0, right: 0 }}
            />
            {showLeads && (
              <YAxis
                yAxisId="leads"
                domain={leadsYDomain}
                allowDataOverflow
                allowDecimals={false}
                tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                width={CHART_Y_AXIS_PX}
              />
            )}
            {showSales && (
              <YAxis
                yAxisId="sales"
                orientation={showLeads ? 'right' : 'left'}
                domain={salesYDomain}
                allowDataOverflow
                tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                tickFormatter={formatAxisCurrency}
                width={showLeads ? CHART_RIGHT_AXIS_PX : CHART_Y_AXIS_PX}
              />
            )}
            <Tooltip
              content={
                <ActivityChartTooltip
                  showLeads={showLeads}
                  showSales={showSales}
                  formatCurrency={formatCurrency}
                />
              }
              wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
              cursor={{ fill: 'rgba(44, 62, 47, 0.04)' }}
            />
            {showLeads && sourceSeries.length > 0 && (
              <>
                {sourceSeries.map((s, i) => (
                  <Bar
                    key={s.dataKey}
                    yAxisId="leads"
                    stackId="leads"
                    dataKey={s.dataKey}
                    name={s.source}
                    fill={s.color}
                    radius={i === sourceSeries.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    maxBarSize={28}
                    isAnimationActive={false}
                  />
                ))}
              </>
            )}
            {showLeads && sourceSeries.length === 0 && (
              <Bar
                yAxisId="leads"
                dataKey="leads"
                name="Leads"
                fill="#CBD5E1"
                radius={[4, 4, 0, 0]}
                maxBarSize={28}
                isAnimationActive={false}
              />
            )}
            {showSales && (
              <>
                <Bar
                  yAxisId="sales"
                  stackId="sales"
                  dataKey="wonRevenue"
                  name="Won revenue"
                  fill="#166534"
                  maxBarSize={28}
                  opacity={0.85}
                  isAnimationActive={false}
                />
                <Bar
                  yAxisId="sales"
                  stackId="sales"
                  dataKey="changeOrderRevenue"
                  name="Change orders"
                  fill="#B45309"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                  opacity={0.9}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="sales"
                  type="monotone"
                  dataKey="wonCount"
                  name="Deals won"
                  stroke="#0F766E"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </>
            )}
            {(showSales || !showLeads || sourceSeries.length === 0) && (
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                iconType="square"
                iconSize={10}
                content={renderLegend}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {showLeads && sourceSeries.length > 0 && (
        <div className="sa-source-legend">
          {sourceSeries.map(s => (
            <span key={s.source} className="sa-source-legend-item">
              <span className="sa-source-dot" style={{ background: s.color }} />
              {s.source}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SalesActivityTimelineChart;
